
import type { ApolloQueryResult } from 'apollo-client'
import { formatDate, getHumanizedTimeFromNow } from '@/utils/date'
import { defineComponent } from 'vue'

// types
import TeamHomeSearchQuery from '~/apollo/queries/repository/teamHomeSearchRepoList.gql'
import ActiveUserMixin from '~/mixins/activeUserMixin'
import RepoListMixin from '~/mixins/repoListMixin'
import { TeamPerms } from '~/types/permTypes'
import {
  Repository,
  RepositoryKindChoices,
  TeamHomeSearchRepoListQuery,
  TeamMemberRoleChoices
} from '~/types/types'
import { resolveNodes } from '~/utils/array'

export default defineComponent({
  name: 'RecentlyActiveRepoList',
  mixins: [ActiveUserMixin, RepoListMixin],
  props: {
    searchQuery: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      repoListLoading: false,
      showAddRepoModal: false,
      searchResults: [] as Repository[],
      searchLoading: false
    }
  },
  async fetch(): Promise<void> {
    this.repoListLoading = true
    const { provider, owner } = this.$route.params

    await this.fetchActiveAnalysisRepoListWithAnalyzers({
      limit: 8,
      login: this.$route.params.owner,
      provider: this.$route.params.provider
    })

    if (process.client)
      this.$localStore.set(
        `${provider}-${owner}`,
        'recently-active-repo-count',
        this.repoWithActiveAnalysisWithAnalyzers?.length ?? 0
      )
    this.repoListLoading = false
  },
  watch: {
    searchQuery(query: string) {
      if (query) {
        this.searchAllRepos(query)
      } else {
        this.searchResults = []
        this.searchLoading = false
      }
    }
  },
  computed: {
    filteredRepos(): Repository[] {
      if (this.searchQuery) return this.searchResults as Repository[]
      return this.repoWithActiveAnalysisWithAnalyzers || []
    },
    isLoading(): boolean {
      return this.repoListLoading || this.searchLoading
    },
    isSearching(): boolean {
      return Boolean(this.searchQuery)
    },
    canActivateRepo(): boolean {
      const role = this.activeDashboardContext.role as TeamMemberRoleChoices
      return this.$gateKeeper.team(TeamPerms.ACTIVATE_ANALYSIS, role)
    },
    loaderCount(): number {
      const { provider, owner } = this.$route.params
      let localCountFromStore
      if (process.client) {
        localCountFromStore = this.$localStore.get(
          `${provider}-${owner}`,
          'recently-active-repo-count'
        ) as number
      }
      return localCountFromStore ?? 8
    }
  },
  methods: {
    getHumanizedTimeFromNow,
    formatDate,
    formatTimestamp(timestamp: string): string {
      const result = getHumanizedTimeFromNow(timestamp)
      const isRelative = /ago$/.test(result) || /^(Earlier today|Yesterday|Last )/.test(result)
      return isRelative ? result : `on ${result}`
    },
    async searchAllRepos(query: string): Promise<void> {
      this.searchLoading = true
      const { provider, owner } = this.$route.params

      try {
        const providerMeta = this.$providerMetaMap[provider]
        if (!providerMeta) return

        const response: ApolloQueryResult<TeamHomeSearchRepoListQuery> =
          await this.$fetchGraphqlData(
            TeamHomeSearchQuery,
            {
              login: owner,
              provider: providerMeta.value,
              first: 8,
              kindIn: [RepositoryKindChoices.Repo, RepositoryKindChoices.Subrepo],
              query
            },
            true
          )

        // Guard against stale results if query changed while request was in flight
        if (this.searchQuery !== query) return

        this.searchResults = resolveNodes(response.data.owner?.repositories) as Repository[]
      } catch (error) {
        if (this.searchQuery !== query) return
        this.$logErrorAndToast(error as Error, 'Failed to search repositories.')
        this.searchResults = []
      } finally {
        this.searchLoading = false
      }
    },
    generateLink({ vcsProvider, ownerLogin, name }: Repository): string {
      const shortcode =
        this.$providerMetaMap[vcsProvider]?.shortcode ||
        this.$providerMetaMap[this.$route.params.provider]?.shortcode ||
        ''
      const login = ownerLogin || this.$route.params.owner
      return '/' + [shortcode, login, name].filter(Boolean).join('/')
    },
    getLatestPR(repo: Repository) {
      return repo.prs?.edges?.[0]?.node ?? null
    },
    getIcon({ isPrivate, kind }: Repository): string {
      if (kind === RepositoryKindChoices.Subrepo) {
        return isPrivate ? 'folder-locked' : 'folder-globe'
      }

      return isPrivate ? 'z-lock' : 'globe'
    }
  }
})
